import * as Yup from 'yup';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';

import { MemoConfirmDialog } from './MemoConfirmDialog';
import { DIALOG_AUTO_FOCUS_ATTRIBUTE } from '../../constants/dialog-auto-focus.constant';
import type { SubmitHandler } from './types/submit-handler.type';

export interface GroupLabelValues {
  readonly isOptional: boolean;
  readonly label: string;
}

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly initialIsOptional: boolean;
  readonly initialLabel: string;
  readonly isDialogOpen: boolean;
  readonly mode: 'create' | 'rename';
  readonly onCloseDialog: () => void;
  readonly onSubmit: SubmitHandler<GroupLabelValues>;
}

const texts = {
  create: {
    title: 'Créer un groupe',
    description: 'Le nouveau groupe sera ajouté à la fin, sans prestation.',
    submitLabel: 'Créer',
  },
  rename: {
    title: 'Modifier le groupe',
    description:
      'Le libellé est affiché dans le bandeau du groupe ; une option du devis peut être retenue ou non par le client.',
    submitLabel: 'Modifier',
  },
} as const;

const validationSchema = Yup.object().shape({
  label: Yup.string().trim().required('Libellé requis'),
});

export const GroupLabelForm = ({
  askMemoBeforeSubmit,
  initialIsOptional,
  initialLabel,
  isDialogOpen,
  mode,
  onCloseDialog,
  onSubmit,
}: Readonly<Props>) => (
  <MemoConfirmDialog<GroupLabelValues>
    askMemoBeforeSubmit={askMemoBeforeSubmit}
    description={texts[mode].description}
    id={`${mode}-service-usages-group`}
    initialValues={{ isOptional: initialIsOptional, label: initialLabel }}
    isDialogOpen={isDialogOpen}
    onCloseDialog={onCloseDialog}
    onSubmit={onSubmit}
    submitLabel={texts[mode].submitLabel}
    title={texts[mode].title}
    validationSchema={validationSchema}
  >
    {({ errors, handleBlur, handleChange, touched, values }) => (
      <>
        <TextField
          error={!!(touched.label && errors.label)}
          fullWidth
          helperText={touched.label ? errors.label : undefined}
          id={`${mode}-service-usages-group-label`}
          label="Libellé du groupe"
          margin="normal"
          name="label"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          slotProps={{ htmlInput: { [DIALOG_AUTO_FOCUS_ATTRIBUTE]: true } }}
          value={values.label}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={values.isOptional}
              id={`${mode}-service-usages-group-is-optional`}
              name="isOptional"
              onChange={handleChange}
            />
          }
          label="Option du devis (le client choisit de la retenir ou non)"
        />
      </>
    )}
  </MemoConfirmDialog>
);
